import { z } from 'zod'
import {
  emailField,
  optionalEmailField,
  optionalPhoneField,
  optionalPostcodeField,
  phoneField,
  postcodeField,
  requiredString,
} from '../Forms/validators'
import type { LeadFormField } from './types'

function fieldSchema({ type, required, label }: LeadFormField): z.ZodTypeAny {
  switch (type) {
    case 'checkbox':
      return required
        ? z.boolean().refine(checked => checked, 'Please tick this box')
        : z.boolean()
    case 'email':
      return required ? emailField : optionalEmailField
    case 'phone':
      return required ? phoneField : optionalPhoneField
    case 'postcode':
      return required ? postcodeField : optionalPostcodeField
    default:
      return required ? requiredString(label) : z.string()
  }
}

/** Validation for the visible fields of a CMS lead form. */
export function buildLeadFormSchema(fields: LeadFormField[]) {
  const visibleFields = fields.filter(field => field.type !== 'hidden')

  const shape = Object.fromEntries(
    visibleFields.map(field => [field.salesforceName, fieldSchema(field)]),
  )

  return z.object(shape).superRefine((values, ctx) => {
    visibleFields.forEach(({ salesforceName, label, requiredWhen }) => {
      if (!requiredWhen || values[requiredWhen.field] !== requiredWhen.value) {
        return
      }
      if (String(values[salesforceName] ?? '').trim()) return

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${label} is required.`,
        path: [salesforceName],
      })
    })
  })
}
